import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Row, Container, Form, Spinner, UncontrolledAccordion } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { useTranslation } from "react-i18next";
import APP_ROUTES from "src/routes/routes";
import { PROCESS_STATUS_NAME } from "src/constant/common";
import { useSetRecoilState } from "recoil";
import { navigationPromptState } from "src/recoil/atoms/global";
import { CustomLink } from "src/components/Common/CustomLink";
import { usePatchedNavigate } from "src/Hooks/usePatchedNavigate";
import MainSection from "../components/MainSection";
import { FORM_NAME } from "src/pages/ProjectInstance/detail";
import Files from "../components/Files";
import ProtectedComponent from "src/components/Permission";
import { truncate } from "lodash";
import { Tooltip } from "react-tooltip";

const InstanceDetailForm = ({
  submitData,
  instanceData,
  fileHistories,
  isFetchingFileHistories,
  getRedirectURL = () => APP_ROUTES.PROJECT_INSTANCE,
}) => {
  const { t } = useTranslation("project-instance");
  const navigate = usePatchedNavigate();
  const [isSubmit, setIsSubmit] = useState(false);
  const [isUpload, setIsUpload] = useState(false);
  const navigationPrompt = useSetRecoilState(navigationPromptState);
  const isAllowEdit =
    instanceData?.[FORM_NAME.MAIN.STATUS] !== PROCESS_STATUS_NAME.DEPLOYING &&
    instanceData?.[FORM_NAME.MAIN.STATUS] !== PROCESS_STATUS_NAME.RUNNING;

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
    reset,
    clearErrors,
  } = useForm({});

  const handleSubmitForm = (data) => {
    navigationPrompt(false);
    setIsSubmit(true);
    const formData = {
      [FORM_NAME.MAIN.NAME]: data[FORM_NAME.MAIN.NAME]?.trim() || "",
      [FORM_NAME.MAIN.DESCRIPTION]: data[FORM_NAME.MAIN.DESCRIPTION]?.trim() || "",
    };

    const currentMdbFile = data[FORM_NAME.FILES.MDB]?.[0];
    const currentOpiFile = data[FORM_NAME.FILES.DASHBOARD_OPI]?.[0];

    if (currentMdbFile && currentMdbFile !== instanceData?.[FORM_NAME.FILES.MDB]) {
      formData[FORM_NAME.FILES.MDB] = currentMdbFile;
    }

    if (currentOpiFile && currentOpiFile !== instanceData?.[FORM_NAME.FILES.DASHBOARD_OPI]) {
      formData[FORM_NAME.FILES.DASHBOARD_OPI] = currentOpiFile;
    }

    return submitData({ data: formData, id: instanceData?.[FORM_NAME.MAIN.ID] }, (isSubmitFailed) => {
      setIsSubmit(false);
      isSubmitFailed && isDirty && navigationPrompt(true);
    });
  };

  useEffect(() => {
    if (isDirty) {
      navigationPrompt(true);
    }
  }, [isDirty]);

  const getTitleBreadcrumb = (id, name) => {
    if (!id || !name) return "";
    return `#${id} - ${truncate(name)}`;
  };

  useEffect(() => {
    if (instanceData) {
      const ensureArray = (value) => {
        if (!value) return [];
        return Array.isArray(value) ? value : [value];
      };
      reset({
        [FORM_NAME.MAIN.NAME]: instanceData?.[FORM_NAME.MAIN.NAME] || "",
        [FORM_NAME.MAIN.DESCRIPTION]: instanceData?.[FORM_NAME.MAIN.DESCRIPTION] || "",
        [FORM_NAME.FILES.MDB]: ensureArray(instanceData?.[FORM_NAME.FILES.MDB]),
        [FORM_NAME.FILES.DASHBOARD_OPI]: ensureArray(instanceData?.[FORM_NAME.FILES.DASHBOARD_OPI]),
      });
    }
  }, [instanceData]);

  return (
    <Container fluid className="project-instance-container">
      <Breadcrumb>
        <BreadcrumbItem>
          <CustomLink to={getRedirectURL(instanceData?.page)} className="link-primary">
            {t("list.title")}
          </CustomLink>
        </BreadcrumbItem>
        <BreadcrumbItem>{t("detail.title")}</BreadcrumbItem>
        <BreadcrumbItem
          active
          data-tooltip-id={`tooltip-breadcrumb-${instanceData?.[FORM_NAME.MAIN.INCREMENT_ID]}`}
          data-tooltip-content={instanceData?.[FORM_NAME.MAIN.NAME] || ""}
        >
          {getTitleBreadcrumb(instanceData?.[FORM_NAME.MAIN.INCREMENT_ID], instanceData?.[FORM_NAME.MAIN.NAME])}
        </BreadcrumbItem>
        <Tooltip
          id={`tooltip-breadcrumb-${instanceData?.[FORM_NAME.MAIN.INCREMENT_ID]}`}
          className="text-break-space max-width-by-px-300"
          place="top"
          positionStrategy="fixed"
          style={{ zIndex: 1080 }}
        />
      </Breadcrumb>

      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <Form onSubmit={handleSubmit(handleSubmitForm)} className="project-instance-form">
                <UncontrolledAccordion defaultOpen={["1"]} stayOpen>
                  <MainSection
                    register={register}
                    errors={errors}
                    instanceData={instanceData}
                    navigationPrompt={navigationPrompt}
                    isAllowEdit={isAllowEdit}
                    setValue={setValue}
                  />
                  <Files
                    register={register}
                    errors={errors}
                    navigationPrompt={navigationPrompt}
                    isAllowEdit={isAllowEdit}
                    setValue={setValue}
                    watch={watch}
                    clearErrors={clearErrors}
                    setIsUpload={setIsUpload}
                    fileHistories={fileHistories}
                    isFetchingFileHistories={isFetchingFileHistories}
                    instanceData={instanceData}
                  />
                </UncontrolledAccordion>

                <div className="d-flex justify-content-center gap-2 mt-2">
                  <span
                    role="button"
                    className="btn btn-secondary width-by-px-200 height-by-px-38"
                    onClick={() => navigate(getRedirectURL(instanceData?.page))}
                  >
                    <span>{t("cancel")}</span>
                  </span>
                  <ProtectedComponent id="project_instance_update">
                    <button
                      className="btn btn-primary width-by-px-200 height-by-px-38"
                      disabled={
                        isSubmit ||
                        isUpload ||
                        !isDirty ||
                        (instanceData?.status === PROCESS_STATUS_NAME.DEPLOYING && instanceData?.status === PROCESS_STATUS_NAME.RUNNING)
                      }
                      type="submit"
                    >
                      {isSubmit ? <Spinner size="sm" /> : <span>{t("submit")}</span>}
                    </button>
                  </ProtectedComponent>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default InstanceDetailForm;
